<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="d-flex align-items-center">
          <button @click="back" class="btn btn-outline-secondary btn-icon-text py-2 px-3 mr-3">
            <i class="mdi mdi-arrow-left mr-1"></i> {{ $t('common.back') }}
          </button>
          <div>
            <h2 class="text-white font-weight-bold mb-1">{{ $t('playerForm.editTitle') }}</h2>
            <p class="text-muted small mb-0">{{ playerStore.name || $t('playerForm.editSub') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Card -->
    <div class="col-12 grid-margin">
      <div class="card border border-secondary">
        <div class="card-body">
          <form class="forms-sample" method="post" @submit.prevent="submitForm">
            <div class="form-group row">
              <div class="col-md-6 mb-3">
                <label for="nameInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('playerForm.playerName') }}</label>
                <input
                  id="nameInput"
                  type="text"
                  class="form-control"
                  v-model="playerStore.name"
                  :placeholder="$currentLocale === 'id' ? 'Contoh: John Doe' : 'e.g. John Doe'"
                  required
                />
              </div>
              <div class="col-md-6 mb-3">
                <label for="dobInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('playerForm.dateOfBirth') }}</label>
                <input
                  id="dobInput"
                  type="date"
                  class="form-control"
                  v-model="playerStore.dob"
                />
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-6 mb-3">
                <label for="emailInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('playerForm.emailAddress') }}</label>
                <input
                  id="emailInput"
                  type="email"
                  class="form-control"
                  v-model="playerStore.email"
                  :placeholder="$currentLocale === 'id' ? 'Contoh: john.doe@perusahaan.com' : 'john.doe@company.com'"
                  required
                />
              </div>
              <div class="col-md-6 mb-3">
                <label for="phone_numberInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('playerForm.phoneNumber') }}</label>
                <input
                  id="phone_numberInput"
                  type="text"
                  class="form-control"
                  v-model="playerStore.phone_number"
                  placeholder="08123456789"
                />
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-6 mb-3">
                <label for="passwordInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('playerForm.updatePassword') }}</label>
                <div class="input-group">
                  <input
                    id="passwordInput"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    v-model="playerStore.password"
                    :placeholder="$t('playerForm.leaveBlankPassword')"
                  />
                  <div class="input-group-append">
                    <button
                      type="button"
                      class="btn btn-dark border-secondary text-muted"
                      @click="showPassword = !showPassword"
                    >
                      <i :class="showPassword ? 'mdi mdi-eye-off text-warning' : 'mdi mdi-eye'"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small text-uppercase font-weight-bold">{{ $t('playerForm.squad') }}</label>
                <select
                  class="form-control"
                  v-model="playerStore.division_id"
                  required
                >
                  <option :value="null" disabled>{{ $t('playerForm.selectSquad') }}</option>
                  <option v-for="option in squadOptions" :key="option.id" :value="option.id">
                    {{ option.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-6 mb-3">
                <label for="positionInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('playerForm.position') }}</label>
                <input
                  id="positionInput"
                  type="text"
                  class="form-control"
                  v-model="playerStore.position"
                  :placeholder="$currentLocale === 'id' ? 'Contoh: Senior Frontend Engineer' : 'e.g. Senior Frontend Engineer'"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small text-uppercase font-weight-bold">{{ $t('playerForm.changePhoto') }}</label>
                <div class="d-flex align-items-center">
                  <div class="avatar-circle mr-3" style="width: 52px; height: 52px; background: rgba(59, 130, 246, 0.15); color: #3b82f6; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 20px; overflow: hidden; border: 1px solid #3f3f46;">
                    <img v-if="previewPhoto" :src="previewPhoto" style="width: 100%; height: 100%; object-fit: cover;" />
                    <img v-else-if="player && player.profile_picture" :src="config.STORAGE_URL + player.profile_picture" style="width: 100%; height: 100%; object-fit: cover;" />
                    <i v-else class="mdi mdi-account"></i>
                  </div>
                  <div class="flex-grow-1">
                    <input
                      type="file"
                      class="form-control"
                      id="customFile"
                      accept="image/*"
                      @change="handleFileChange"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" @click="back" class="btn btn-outline-secondary px-4 py-2 mr-2" :disabled="loading">
                {{ $t('common.back') }}
              </button>
              <button type="submit" class="btn btn-primary px-4 py-2 font-weight-bold" :disabled="loading">
                <span class="spinner-border spinner-border-sm mr-1" v-if="loading" role="status"></span>
                <i class="mdi mdi-check-all mr-1" v-else></i>
                {{ $t('common.save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { updatePlayer, getDetailPlayer, getSquad } from "@/services/playerService";
import config from "@/config/config";
import Swal from "sweetalert2";

export default {
  name: "EditPlayer",
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    showPassword: false,
    playerStore: {
      company_id: null,
      name: "",
      email: "",
      phone_number: "",
      password: "",
      division_id: null,
      profile_picture: null,
      role: "player",
      position: "",
      dob: "",
    },
    squadOptions: [],
    player: null,
    previewPhoto: null,
    config,
    loading: false,
  }),
  mounted() {
    this.fetchDetailPlayer();
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        const response = await getSquad();
        this.squadOptions = response.data || [];
      } catch (err) {
        console.error(err);
      }
    },
    async fetchDetailPlayer() {
      try {
        const response = await getDetailPlayer(this.id);
        const data = response.data || {};
        this.player = data;
        this.playerStore.company_id = data.company_id;
        this.playerStore.division_id = data.division_id;
        this.playerStore.name = data.name || "";
        this.playerStore.email = data.email || "";
        this.playerStore.phone_number = data.phone_number || "";
        this.playerStore.password = "";
        this.playerStore.dob = data.dob || "";
        this.playerStore.position = data.position || "";
        if (data.role) {
          this.playerStore.role = data.role;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async submitForm() {
      try {
        this.loading = true;
        // Don't send empty password
        const payload = { ...this.playerStore };
        if (!payload.password) {
          delete payload.password;
        }

        await updatePlayer(this.id, payload);
        Swal.fire({
          icon: "success",
          title: this.$t('common.success'),
          text: this.$t('playerForm.updatedSuccess'),
          confirmButtonColor: "#3b82f6",
        });

        this.$router.push('/player');
      } catch (error) {
        const errors = error.response?.data?.errors;
        const errMsg = (errors && typeof errors === 'object')
          ? Object.values(errors).flat()[0]
          : (error.response?.data?.message || error.message || (this.$currentLocale === 'id' ? "Gagal memperbarui data player" : "Failed to update player data"));
        Swal.fire({
          icon: "error",
          title: this.$t('common.error'),
          text: errMsg,
          confirmButtonColor: "#3b82f6",
        });
      } finally {
        this.loading = false;
      }
    },
    back() {
      this.$router.go(-1);
    },
    handleFileChange(event) {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        this.playerStore.profile_picture = file;
        this.previewPhoto = URL.createObjectURL(file);
      } else {
        this.playerStore.profile_picture = null;
        this.previewPhoto = null;
      }
    },
  },
};
</script>
